import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from "@/contexts/AuthContext";
import { useLayout } from "@/contexts/LayoutContext";
import { supabase } from "@/integrations/supabase/client";
import { 
  SidebarContent, 
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { 
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Lock,
  AlertCircle,
  Folder,
  FolderOpen,
  Info,
  CheckCircle2,
  Circle,
  BookOpen,
  FilmIcon,
  HelpCircle,
  Home,
  Loader2,
} from 'lucide-react';
import { toast } from "@/components/ui/use-toast";
import { cn } from '@/lib/utils';
import UserSelector from '@/components/workspace/UserSelector';
import { useAppState } from '@/hooks/use-app-state';

interface CursoSidebarProps {
  openReminderDialog?: () => void;
  currentStage?: number;
}

type StageTableName = 'stage1_responses' | 'stage2_responses' | 'stage3_responses' | 'stage4_responses';

const getTableNameForStage = (stageNum: number): StageTableName => {
  switch(stageNum) {
    case 1: return 'stage1_responses';
    case 2: return 'stage2_responses';
    case 3: return 'stage3_responses';
    case 4: return 'stage4_responses';
    default: throw new Error(`Invalid stage number: ${stageNum}`);
  }
};

const CursoSidebar: React.FC<CursoSidebarProps> = ({ 
  openReminderDialog,
  currentStage = 0
}) => {
  const { user, isAdmin } = useAuth();
  const { currentStep, navigateToStep } = useLayout();
  
  // Add debugging for current stage and step
  console.log(`CursoSidebar - Current Stage: ${currentStage}, Current Step: ${currentStep}`);
  
  const navigate = useNavigate();
  const [expandedStage, setExpandedStage] = useState<number | null>(currentStage > 0 ? currentStage : null);
  const [programFolderOpen, setProgramFolderOpen] = useState(true);
  const [onboardingFolderOpen, setOnboardingFolderOpen] = useState(false);
  const [reminderCount, setReminderCount] = useState(0);
  const { selectedViewUser, setSelectedViewUser } = useAppState();
  const [stagesProgress, setStagesProgress] = useState<{
    stage1: number;
    stage2: number;
    stage3: number;
    stage4: number;
  }>({
    stage1: 0,
    stage2: 0,
    stage3: 0,
    stage4: 0
  });
  const [userProgress, setUserProgress] = useState<{
    stage_1: string;
    stage_2: string;
    stage_3: string;
    stage_4: string;
  } | null>(null);
  const [overallProgress, setOverallProgress] = useState(0);
  const [allStagesLocked, setAllStagesLocked] = useState(false);
  const [stepsCompletion, setStepsCompletion] = useState<{ [stageNum: number]: Record<number, boolean> }>({});

  const currentUserId = selectedViewUser || user?.id || null;

  // Always ensure the current stage is expanded when component mounts or currentStage changes
  useEffect(() => {
    if (currentStage > 0) {
      setExpandedStage(currentStage);
    }
  }, [currentStage]);
  
  // Keep track of URL location to update expanded stage when navigation occurs
  const location = useLocation();
  // Track the URL step parameter directly in state to make sure we're in sync
  const [urlStep, setUrlStep] = useState<number>(0);
  
  useEffect(() => {
    const pathname = location.pathname;
    let stageFromPath = 0;
    
    if (pathname.includes('stage-one')) stageFromPath = 1;
    else if (pathname.includes('stage-two')) stageFromPath = 2;
    else if (pathname.includes('stage-three')) stageFromPath = 3;
    else if (pathname.includes('stage-four')) stageFromPath = 4;
    
    // Check for step parameter in URL query string
    const searchParams = new URLSearchParams(location.search);
    const stepParam = searchParams.get('step');
    const stepFromUrl = stepParam ? parseInt(stepParam, 10) : 0;
    
    console.log(`URL parsed: stage=${stageFromPath}, step=${stepFromUrl}, currentStep=${currentStep}`);
    
    // Update urlStep for use in determining active steps
    setUrlStep(stepFromUrl);
    
    if (stageFromPath > 0) {
      setExpandedStage(stageFromPath);
    }
    
    if (stepParam) {
      console.log(`URL contains step parameter: ${stepParam}, current stage: ${stageFromPath}`);
      
      // Ensure the stage is expanded when step parameter is present
      if (stageFromPath > 0) {
        setExpandedStage(stageFromPath);
      }
    }
  }, [location]);

  // Log when currentStep changes
  useEffect(() => {
    console.log(`CursoSidebar - currentStep changed to: ${currentStep}`);
    
    if (currentStep > 0 && currentStage > 0) {
      // Force the sidebar to show the current stage expanded
      setExpandedStage(currentStage);
      console.log(`CursoSidebar - Setting expanded stage to: ${currentStage}`);
    }
  }, [currentStep, currentStage]);

  useEffect(() => {
    if (user?.id) {
      fetchReminders();
      fetchUserProgress();
    }
  }, [user, selectedViewUser]);

  useEffect(() => {
    if (user?.id && expandedStage !== null) {
      fetchUserProgress();
    }
  }, [expandedStage]);

  const fetchReminders = async () => {
    if (!user) return;
    
    try {
      const now = new Date();
      
      const { count, error: countError } = await supabase
        .from('reminders')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .gte('due_date', now.toISOString());
        
      if (countError) throw countError;
      
      setReminderCount(count || 0);
    } catch (error) {
      console.error('Error fetching reminder count:', error);
    }
  };

  const fetchUserProgress = async () => {
    if (!user) return;

    try {
      console.log(`Fetching progress data for user: ${selectedViewUser || 'current user'}`);
      
      const userId = currentUserId;
      
      if (!userId) {
        console.log('No valid user ID for progress data');
        return;
      }
      
      console.log(`Using user ID ${userId} for progress data`);
      
      const { data: progress } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (progress) {
        setUserProgress(progress);
        
        const allLocked = 
          progress.stage_1 === 'locked' && 
          progress.stage_2 === 'locked' && 
          progress.stage_3 === 'locked' && 
          progress.stage_4 === 'locked';
        
        setAllStagesLocked(allLocked);
      }

      const stages = [1, 2, 3, 4].filter(stage => 
        !progress || progress[`stage_${stage}`] !== 'locked'
      );
      
      const progressData: Record<string, number> = {};
      
      for (const stage of stages) {
        const tableName = getTableNameForStage(stage);
        
        const shouldFetch = currentStage === stage || expandedStage === stage;
        
        if (shouldFetch) {
          const { data: responses } = await supabase
            .from(tableName)
            .select('*')
            .eq('user_id', userId);

          const progress = calculateProgress(responses);
          progressData[`stage${stage}`] = progress;

          // Compute per-step completion and update state
          const completionMap = computeStepCompletion(responses);
          if (Object.keys(completionMap).length > 0) {
            setStepsCompletion((prev) => ({
              ...prev,
              [stage]: {
                ...(prev[stage] || {}),
                ...completionMap,
              },
            }));
          }
        } else {
          progressData[`stage${stage}`] = stagesProgress[`stage${stage}`] || 0;
        }
      }

      setStagesProgress({
        stage1: progressData.stage1 || 0,
        stage2: progressData.stage2 || 0,
        stage3: progressData.stage3 || 0,
        stage4: progressData.stage4 || 0
      });

      const availableStages = Object.keys(progressData).length;
      if (availableStages > 0) {
        const overallPercentage = Math.round(
          Object.values(progressData).reduce((sum, progress) => sum + progress, 0) / availableStages
        );
        
        setOverallProgress(overallPercentage);
      }
    } catch (error) {
      console.error('Error fetching user progress:', error);
    }
  };

  const calculateProgress = (responses: any[] | null) => {
    // Safe handling for null or undefined responses
    if (!responses || !Array.isArray(responses)) return 0;
    if (!responses) return 0;
    const completed = responses.filter(r => r.status === 'Listo').length;
    return responses.length > 0 ? Math.round((completed / responses.length) * 100) : 0;
  };

  const computeStepCompletion = (responses: any[] | null) => {
    if (!responses || !Array.isArray(responses)) return {};

    const stepStats: Record<number, { total: number; completed: number }> = {};

    responses.forEach((r) => {
      const step = r.step_number;
      if (!stepStats[step]) {
        stepStats[step] = { total: 0, completed: 0 };
      }
      stepStats[step].total += 1;
      if (r.status === "Listo") {
        stepStats[step].completed += 1;
      }
    });

    const completionMap: Record<number, boolean> = {};
    Object.entries(stepStats).forEach(([step, { total, completed }]) => {
      completionMap[Number(step)] = total > 0 && completed === total;
    });

    return completionMap;
  };

  const getStageStatus = (stageNumber: number) => {
    // Show Locked status first if stage is not yet unlocked
    if (!isStageUnlocked(stageNumber)) {
      return (
        <div className="text-red-500 flex items-center">
          <Lock className="w-4 h-4 mr-2" />
          <span>Bloqueado</span>
        </div>
      );
    }

    // Determine completion based on percentage progress calculated for the stage.
    // A stage is considered "Completado" ONLY when absolutely all sus pasos have
    // 100 % de avance (es decir, todas las tablas de todos los pasos están con status "Listo").
    const stageKey = `stage${stageNumber}` as keyof typeof stagesProgress;
    const progressPercent = stagesProgress[stageKey] ?? 0;

    if (progressPercent === 100) {
      return (
        <div className="text-green-600 flex items-center">
          <CheckCircle2 className="w-4 h-4 mr-2" />
          <span>Completado</span>
        </div>
      );
    }

    // Otherwise, it's unlocked but not 100 % → show "En progreso" with spinner
    return (
      <div className="text-[#2E80EC] flex items-center">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        <span>En progreso</span>
      </div>
    );
  };

  const handleNavigateToStep = (stageNumber: number, stepNumber: number) => {
    // Validate input parameters
    if (!stageNumber || !stepNumber) {
      console.error('Invalid stage or step number', { stageNumber, stepNumber });
      return;
    }
    if (!isStageUnlocked(stageNumber)) {
      toast({
        variant: "destructive",
        title: "Etapa bloqueada",
        description: "Esta etapa está bloqueada actualmente y no se puede acceder a ella.",
      });
      return;
    }
    
    // Always ensure the stage being navigated to is expanded in the sidebar
    setExpandedStage(stageNumber);
    
    // Update the layout context
    navigateToStep(stageNumber, stepNumber);
    
    const stagePath = stageNumber === 1 ? '/stage-one' : 
                      stageNumber === 2 ? '/stage-two' : 
                      stageNumber === 3 ? '/stage-three' : 
                      stageNumber === 4 ? '/stage-four' : '/';
    
    navigate(`${stagePath}?step=${stepNumber}`, { 
      replace: true,
      state: { isChangingStage: true }
    });
  };

  const isStageUnlocked = (stageNumber: number) => {
    if (!userProgress) return stageNumber === 1;
    const stageStatus = userProgress[`stage_${stageNumber}` as keyof typeof userProgress];
    return stageStatus !== 'locked';
  };

  const toggleExpandStage = (stageNumber: number, event: React.MouseEvent) => {
    if (!isStageUnlocked(stageNumber)) {
      toast({
        variant: "destructive",
        title: "Etapa bloqueada",
        description: "Esta etapa está bloqueada actualmente y no se puede acceder a ella.",
      });
      event.stopPropagation();
      return;
    }
    
    // Toggle the expanded state - if it's already expanded, collapse it, otherwise expand it
    setExpandedStage(expandedStage === stageNumber ? null : stageNumber);
    event.stopPropagation();
  };

  const handleFolderToggle = () => {
    if (!allStagesLocked) {
      setProgramFolderOpen(!programFolderOpen);
    }
  };

  const handleOnboardingFolderToggle = () => {
    setOnboardingFolderOpen(!onboardingFolderOpen);
  };

  const handleNavigateToIntro = () => {
    navigate('/intro');
  };

  const handleNavigateToResource = (path: string) => {
    navigate(path);
  };

  // Handle user selection change from UserSelector component
  const handleUserChange = (userId: string | null) => {
    console.log(`User changed to: ${userId} in CursoSidebar`);
    setSelectedViewUser(userId);
    // Refetch data for the newly selected user
    fetchUserProgress();
  };

  const stage1Steps = {
    "1": "Paso 1: Elementos de Línea",
    "2": "Paso 2: Categorías de Productos",
    "3": "Paso 3: Tipos de Cliente",
    "4": "Paso 4: Propiedades CRM",
    "5": "Paso 5: Motivos de Descarte",
    "6": "Paso 6: Equipos y Asignación",
    "7": "Paso 7: Generación de Demanda"
  };

  const stage2Steps = {
    "1": "Paso 1: Herramientas Comerciales",
    "2": "Paso 2: FODA Proceso de Ventas",
    "3": "Paso 3: KPIs Críticos",
    "4": "Paso 4: Requerimientos",
    "5": "Paso 5: Diseño del Pipeline",
    "6": "Paso 6: Nuevo Pipeline",
    "7": "Paso 7: Negocios Existentes"
  };

  const stage3Steps = {
    "1": "Paso 1: Creación manual",
    "2": "Paso 2: Personalización UI",
    "3": "Paso 3: Reglas básicas",
    "4": "Paso 4: Transferencia info",
    "5": "Paso 5: Oportunidades",
    "6": "Paso 6: Definir Triggers",
    "7": "Paso 7: Sistema Monitoreo"
  };

  const stage4Steps = {
    "1": "Paso 1: Reportería para Visibilidad",
    "2": "Paso 2: Reportería para Accionables",
    "3": "Paso 3: Reportería para Insights",
    "4": "Paso 4: Reportería de Gestión",
    "5": "Paso 5: Diseño de Cadencias"
  };

  // Get the current URL step explicitly at the component level
  const searchParams = new URLSearchParams(location.search);
  const urlStepParam = searchParams.get('step');
  const urlStepNumber = urlStepParam ? parseInt(urlStepParam, 10) : 0;
  
  // Get the current URL stage
  const pathname = location.pathname;
  let urlStageNumber = 0;
  if (pathname.includes('stage-one')) urlStageNumber = 1;
  else if (pathname.includes('stage-two')) urlStageNumber = 2;
  else if (pathname.includes('stage-three')) urlStageNumber = 3;
  else if (pathname.includes('stage-four')) urlStageNumber = 4;

  console.log(`CursoSidebar direct URL parsing: Stage=${urlStageNumber}, Step=${urlStepNumber}`);

  return (
    <SidebarContent>
      <>
        <div className="px-4 mb-4">
          {isAdmin && (
            <div className="mb-4 mt-1">
              <UserSelector
                currentUserId={currentUserId}
                onSelect={handleUserChange}
              />
            </div>
          )}
          
          <button 
            className="flex items-center justify-between w-full py-3 px-4 text-sm font-medium rounded-lg transition-colors 
              bg-[#E8F0FE] text-[#4C8EF1] border-[#4C8EF1]/30 hover:bg-[#D8E6FD] mb-2"
            onClick={() => navigate('/booking-help')}
          >
            <div className="flex items-center">
              <HelpCircle className="h-4 w-4 mr-2" />
              <span className="font-medium">Agenda una reunión acá</span>
            </div>
            <ChevronRight className="h-4 w-4" />
          </button>
          
          <div 
            className="flex items-center justify-between p-2 rounded-md border border-gray-200 bg-gray-50 cursor-pointer mb-2"
            onClick={handleOnboardingFolderToggle}
          >
            {onboardingFolderOpen ? (
              <FolderOpen className="h-5 w-5 text-[#4A5568] mr-2" />
            ) : (
              <Folder className="h-5 w-5 text-[#4A5568] mr-2" />
            )}
            <span className="flex-1 font-medium">Onboarding & Hubspot</span>
            {onboardingFolderOpen ? (
              <ChevronUp className="h-4 w-4 text-[#4A5568]" />
            ) : (
              <ChevronDown className="h-4 w-4 text-[#4A5568]" />
            )}
          </div>

          {onboardingFolderOpen && (
            <div className="pl-2 mb-3">
              <div className="space-y-2 mt-2">
                <button 
                  className="flex items-center w-full p-2 text-sm rounded-md border border-gray-200 text-gray-800 hover:bg-gray-50"
                  onClick={() => handleNavigateToResource('/onboarding-basico')}
                >
                  <BookOpen className="h-4 w-4 mr-2 text-green-500" />
                  <span>Onboarding básico</span>
                </button>
                
                {isAdmin && (
                  <button 
                    className="flex items-center w-full p-2 text-sm rounded-md border border-gray-200 text-gray-800 hover:bg-gray-50"
                    onClick={() => handleNavigateToResource('/tutoriales')}
                  >
                    <FilmIcon className="h-4 w-4 mr-2 text-purple-500" />
                    <span>Tutoriales</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {programFolderOpen && !allStagesLocked && (
            <div className="pl-2">
              <button 
                onClick={() => navigate('/intro')}
                className="flex items-center w-full p-2 mb-2 text-sm rounded-md bg-[#EFF6FF] text-[#2E80EC] hover:bg-[#E6F0FF]"
              >
                <Info className="h-4 w-4 mr-2" />
                <span>Sobre este programa</span>
              </button>

              <div className="space-y-3">
                {[1, 2, 3, 4].map((stageNum) => (
                  <div key={stageNum}>
                    <div 
                      className={`flex items-center gap-2 p-2 rounded-md ${
                        !isStageUnlocked(stageNum) ? 'opacity-60 cursor-not-allowed' : 
                        'cursor-pointer hover:bg-gray-100'
                      } ${expandedStage === stageNum ? 'bg-gray-100' : ''}`}
                      onClick={(e) => toggleExpandStage(stageNum, e)}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                        !isStageUnlocked(stageNum) ? 'bg-gray-400' : 
                        currentStage === stageNum ? 'bg-[#148aff]' : 'bg-[#2E80EC]'
                      } text-white text-sm font-medium flex-shrink-0`}>
                        {!isStageUnlocked(stageNum) ? <Lock className="h-3 w-3" /> : stageNum}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium whitespace-nowrap">
                          {stageNum === 1 ? "Identificación de clientes" :
                           stageNum === 2 ? "Estructuración del Proceso" :
                           stageNum === 3 ? "Automatización y Eficiencia" :
                           "Reportería y Cadencias"}
                        </div>
                        <div className="text-sm">
                          {getStageStatus(stageNum)}
                        </div>
                      </div>
                      {isStageUnlocked(stageNum) && (
                        expandedStage === stageNum ? 
                          <ChevronDown className="h-5 w-5 text-[#4A5568]" /> : 
                          <ChevronRight className="h-5 w-5 text-[#4A5568]" />
                      )}
                    </div>
                    
                    {expandedStage === stageNum && isStageUnlocked(stageNum) && (
                      <div className="ml-9 mt-2 space-y-2">
                        {Object.entries(
                            stageNum === 1 ? stage1Steps || {} :
                            stageNum === 2 ? stage2Steps || {} :
                            stageNum === 3 ? stage3Steps || {} :
                            stage4Steps || {}
                          ).map(([stepNumber, title]) => {
                          // Ensure title is always a string
                          const safeTitle = title ? String(title) : `Paso ${stepNumber}`;
                          const stepNum = parseInt(stepNumber);
                          
                          // Prioritize URL parameters for determining the active step
                          const isCurrentStep = (stageNum === urlStageNumber && stepNum === urlStepNumber);
                          
                          console.log(`Rendering step: Stage ${stageNum}, Step ${stepNum}, urlStage: ${urlStageNumber}, urlStep: ${urlStepNumber}, isActive: ${isCurrentStep}`);
                          
                          // Force styles with !important flag for active step
                          const buttonStyle = isCurrentStep ? {
                            backgroundColor: '#148aff', 
                            color: 'white !important'
                          } : undefined;
                          
                          const isCompleted = stepsCompletion[stageNum]?.[stepNum] || false;
                          
                          // Use a specific component for the step button to control rendering
                          return (
                            <StepButton
                              key={`stage${stageNum}-step-${stepNumber}-${isCurrentStep ? 'active' : 'inactive'}`}
                              isActive={isCurrentStep}
                              title={safeTitle}
                              onClick={() => handleNavigateToStep(stageNum, stepNum)}
                              completed={isCompleted}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </>
    </SidebarContent>
  );
};

interface StepButtonProps {
  isActive: boolean;
  title: string;
  onClick: () => void;
  completed?: boolean;
}

const StepButton: React.FC<StepButtonProps> = ({ isActive, title, onClick, completed }) => {
  // Completely new approach with most explicit styling possible
  // Use a complete set of inline styles for both active and inactive states
  return (
    <button
      onClick={onClick}
      data-active={isActive ? 'true' : 'false'}
      style={{
        backgroundColor: isActive ? '#148aff' : '#ffffff',
        color: isActive ? '#ffffff' : '#4b5563', // text-gray-800
        borderRadius: '0.375rem', // equivalent to rounded-md
        fontWeight: isActive ? 'bold' : 'normal',
        padding: '0.5rem', // equivalent to p-2
        textAlign: 'left',
        fontSize: '0.875rem', // equivalent to text-sm
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: isActive ? '1px solid #148aff' : '1px solid #e5e7eb', // border-gray-200
        marginBottom: '0.5rem',
        cursor: 'pointer',
        // Add transition for smooth hover effect
        transition: 'background-color 0.2s, color 0.2s, border-color 0.2s',
      }}
      onMouseOver={(e) => {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = '#f9fafb'; // hover:bg-gray-50
        }
      }}
      onMouseOut={(e) => {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = '#ffffff';
        }
      }}
    >
      <span style={{ color: isActive ? '#ffffff' : 'inherit', display: 'flex', alignItems: 'center' }}>
        {/* If the step is completed show a check.  
            • Inactive step  -> green check (text-green-500)  
            • Active step    -> white check so it pops on the blue background */}
        {completed && (
          <CheckCircle2
            className={
              `h-4 w-4 mr-2 ${isActive ? 'text-white' : 'text-green-500'}`
            }
          />
        )}
        {title}
      </span>
    </button>
  );
};

export default CursoSidebar;
